import React, { useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  TextField,
  Button,
  Paper,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import emailjs from '@emailjs/browser';
import { wolvesLabsColors } from '../../theme/wolvesLabsTheme';
import ContactModal from './ContactModal';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'loading' | 'success' | 'error'>('loading');
  const [modalTitle, setModalTitle] = useState('');
  const [modalMessage, setModalMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Mostrar modal de loading
    setModalType('loading');
    setModalTitle('Enviando mensaje...');
    setModalMessage('Por favor espera mientras procesamos tu solicitud.');
    setModalOpen(true);
    
    try {
      // Configuración de EmailJS
      const serviceId = 'service_tomelzf';
      const templateId = 'template_a3ruhd9';
      const publicKey = '9O8aaQXGtpaqlYxxo';
      
      // Datos del formulario
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        company: formData.company,
        message: formData.message,
        to_email: 'luis.flores@thewolveslabs.com',
      };

      // Enviar email
      await emailjs.send(serviceId, templateId, templateParams, publicKey);
      
      console.log('Correo enviado exitosamente:', formData);
      
      // Mostrar modal de éxito
      setModalType('success');
      setModalTitle('¡Mensaje enviado!');
      setModalMessage('Gracias por contactarnos. Nos pondremos en contacto contigo pronto.');
      
      // Limpiar formulario
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: '',
      });
      
      // Cerrar modal después de 3 segundos
      setTimeout(() => {
        setModalOpen(false);
      }, 3000);
      
    } catch (error) {
      console.error('Error al enviar el correo:', error);
      
      // Mostrar modal de error
      setModalType('error');
      setModalTitle('Error al enviar');
      setModalMessage('Hubo un problema al enviar tu mensaje. Por favor, inténtalo de nuevo o contáctanos directamente.');
      
      // Cerrar modal después de 5 segundos
      setTimeout(() => {
        setModalOpen(false);
      }, 5000);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const contactInfo = [
    {
      icon: <EmailIcon />,
      title: 'Email',
      value: 'luis.flores@thewolveslabs.com',
      link: 'mailto:luis.flores@thewolveslabs.com',
    },
    {
      icon: <PhoneIcon />,
      title: 'Teléfono',
      value: '+52 (123) 456-7890',
      link: 'tel:+521234567890',
    },
    {
      icon: <LocationIcon />,
      title: 'Ubicación',
      value: 'México',
      link: null,
    },
  ];

  return (
    <Box
      id="contact"
      sx={{
        py: { xs: 8, md: 12 },
        background: wolvesLabsColors.gradients.hero,
        color: 'white',
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 800,
              mb: 2,
              color: 'white',
            }}
          >
            Contáctanos
          </Typography>
          <Typography
            variant="h6"
            sx={{
              opacity: 0.9,
              maxWidth: '700px',
              mx: 'auto',
              lineHeight: 1.8,
            }}
          >
            ¿Tienes un proyecto en mente? Nos encantaría escucharlo. 
            Contáctanos y hagamos realidad tu idea.
          </Typography>
        </Box>

        <Grid container spacing={6}>
          {/* Formulario de contacto */}
          <Grid item xs={12} md={7}>
            <Paper
              elevation={10}
              sx={{
                p: 4,
                borderRadius: 0,
                backgroundColor: 'white',
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  mb: 3,
                  color: wolvesLabsColors.primary,
                }}
              >
                Envíanos un Mensaje
              </Typography>

              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Nombre Completo"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Teléfono"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Empresa (Opcional)"
                      value={formData.company}
                      onChange={(e) => handleChange('company', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Cuéntanos sobre tu proyecto"
                      multiline
                      rows={6}
                      value={formData.message}
                      onChange={(e) => handleChange('message', e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      fullWidth
                      endIcon={<SendIcon />}
                      sx={{
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        background: wolvesLabsColors.gradients.tech,
                        '&:hover': {
                          boxShadow: '0 8px 20px rgba(233, 69, 96, 0.4)',
                        },
                      }}
                    >
                      Enviar Mensaje
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>

          {/* Información de contacto */}
          <Grid item xs={12} md={5}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
              {contactInfo.map((info, index) => (
                <Paper
                  key={index}
                  elevation={0}
                  component={info.link ? 'a' : 'div'}
                  href={info.link || undefined}
                  sx={{
                    p: 3,
                    borderRadius: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    transition: 'all 0.3s ease',
                    textDecoration: 'none',
                    color: 'inherit',
                    cursor: info.link ? 'pointer' : 'default',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.15)',
                      transform: 'translateX(10px)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{
                        color: 'white',
                        mr: 2,
                        p: 1.5,
                        borderRadius: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      }}
                    >
                      {info.icon}
                    </Box>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          opacity: 0.8,
                          mb: 0.5,
                          fontSize: '0.85rem',
                        }}
                      >
                        {info.title}
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                        }}
                      >
                        {info.value}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              ))}

              {/* Horario */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: 'white' }}>
                  Horario de Atención
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                  Lunes a Viernes: 9:00 AM - 6:00 PM
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Sábados: 10:00 AM - 2:00 PM
                </Typography>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Modal de estado */}
      <ContactModal
        open={modalOpen}
        type={modalType}
        title={modalTitle}
        message={modalMessage}
      />

      {/* Snackbar de confirmación */}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={4000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSnackbar(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          ¡Mensaje enviado! Nos pondremos en contacto contigo pronto.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Contact;

